import React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Button, Drawer, Select, message, Alert, Radio } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import AvatarUploader, {
  IFirebaseImage,
} from '../avatarUploader/avatarUploader';
import { CREATE_DRIVER } from './mutations';
import { checkEmailExistsInFirebase } from '../../utils/firebase';
import { FETCH_DRIVING_COMPANIES } from './queries';
import { getClient } from '../../apollo';
import { ALL_DIAL_CODES } from '../../constants/countrycodes';
import { validateEmail } from '../../utils/email';
import { containsOnlyWhiteSpaces } from '../../utils/string';
import { validatePassword } from '../../utils/password';
import { sanitizeObject } from '../../utils/object';
import { validatePhone } from '../../utils/phone';
import { LoginMethod } from '../../graphql/datamodel.gen';
import StickyFooterFormButtons from '../StickyFooterFormButtons/StickyFooterFormButtons';

const { Option } = Select;

interface IFormComponentProps extends FormComponentProps {
  extraProps: {
    onSubmit: any;
  };
  drivingCompanies: any[];
}

const fetchDrivingCompanies = async () => {
  const response = await getClient().query({
    query: FETCH_DRIVING_COMPANIES,
    fetchPolicy: 'no-cache',
  });

  return response.data.drivingCompanies || [];
};

const createDriver = async (
  loginMethod: LoginMethod,
  name: string,
  type: string,
  status: string,
  password?: string,
  profilePicURI?: string,
  drivingCompanyId?: string,
  phone?: string,
  email?: string,
) => {
  const response = await getClient().mutate({
    mutation: CREATE_DRIVER,
    variables: {
      email,
      password,
      phone,
      profilePicURI,
      name,
      type,
      status,
      drivingCompanyId,
      loginMethod,
    },
  });

  return response.data.createDriver;
};

interface DriverCreationFormProps extends FormComponentProps {
  extraProps: {
    onSubmit: any;
  };
  drivingCompanies: any[];
}
type SignupMethod = 'email' | 'phone';

interface MyFormState {
  emailRequired: boolean;
  signupMethod: SignupMethod;
  validationError?: string;
  assignToDrivingCompany: boolean;
  creationLoading: boolean;
  profilePicURI: string;
}

class DriverCreationForm extends React.Component<
  DriverCreationFormProps,
  MyFormState
> {
  state = {
    emailRequired: false,
    signupMethod: 'email' as SignupMethod,
    validationError: undefined,
    assignToDrivingCompany: true,
    creationLoading: false,
    profilePicURI: undefined,
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        const sanitizedValues = sanitizeObject<any>(values);

        const {
          prefix,
          email,
          password,
          phone,
          name,
          type,
          status,
          drivingCompanyId,
        } = sanitizedValues;

        try {
          const computedPhone = phone ? `${prefix}${phone}` : undefined;
          this.setState({
            creationLoading: true,
          });
          message.loading({
            content: 'Creando conductor',
            key: 'createDriver',
          });
          const driverCreated = await createDriver(
            this.state.signupMethod.toUpperCase() as LoginMethod,
            name,
            type,
            status,
            password,
            this.state.profilePicURI,
            drivingCompanyId,
            computedPhone,
            email,
          );

          if (driverCreated) {
            message.success({
              content: 'Conductor creado!',
              key: 'createDriver',
            });
            this.setState({
              creationLoading: false,
            });
            this.props.extraProps.onSubmit(true);
          } else {
            message.error({
              content: 'Error creando el conductor, por favor prueba de nuevo',
              key: 'createDriver',
            });
            this.props.extraProps.onSubmit(false);
          }
        } catch (error) {
          message.error({
            content: 'Error creando el conductor, por favor prueba de nuevo',
            key: 'createDriver',
          });
          console.error('Error creating driver', error);
          this.props.extraProps.onSubmit(false);
        } finally {
          this.setState({
            creationLoading: false,
          });
          setTimeout(() => {
            message.destroy();
          }, 2000);
        }
      }
    });
  };

  emailValidator = async (
    rule: any,
    value: any,
    callback: (arg0?: string) => void,
  ) => {
    const invalid = await validateEmail(value, {
      isRequired: { enabled: this.state.signupMethod === 'email' },
      checkAgainstFirebase: { enabled: true },
    });

    if (invalid) return callback(invalid);
    return callback();
  };

  passwordValidator = (
    rule: any,
    value: any,
    callback: { (arg0: string): void; (): void },
  ) => {
    const invalid = validatePassword(value, {
      required: this.state.signupMethod === 'email',
    });
    if (invalid) return callback(invalid);
    return callback();
  };

  phoneValidator = (
    rule: any,
    value: any,
    callback: { (arg0: string): void; (): void },
  ) => {
    const invalid = validatePhone(value, {
      isRequired: {
        enabled: this.state.signupMethod === 'phone',
      },
    });
    if (invalid) {
      return callback(invalid);
    }
    return callback();
  };

  onRadioGroupChange = (e) => {
    this.setState(
      {
        signupMethod: e.target.value,
      },
      () => {
        this.props.form.validateFields();
      },
    );
  };

  normFile = (e: { fileList: any }) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  onProfilePicUploadComplete = (image: IFirebaseImage) => {
    this.setState({
      profilePicURI: image.reference,
    });
  };

  render(): React.ReactNode {
    const { getFieldDecorator } = this.props.form;

    const prefixSelector = getFieldDecorator('prefix', {
      initialValue: '+34',
    })(
      <Select showSearch={true} style={{ width: 90 }}>
        {ALL_DIAL_CODES.map((dialCode) => (
          <Option key={dialCode} value={dialCode}>
            {dialCode}
          </Option>
        ))}
      </Select>,
    );

    return (
      <Form onSubmit={this.handleSubmit} layout={'vertical'}>
        <Form.Item label="Nombre y apellido(s)" required={true}>
          {getFieldDecorator('name', {
            rules: [
              {
                required: true,
                message: 'Por favor, introduce nombre y apellido(s)',
              },
            ],
          })(<Input />)}
        </Form.Item>
        <Form.Item required={true} label={'Método de inicio de sesión'}>
          {getFieldDecorator('signupMethod', {
            initialValue: this.state.signupMethod,
            rules: [
              {
                required: true,
                message:
                  'Por favor, selecciona un método de inicio de sesión para el conductor',
              },
            ],
          })(
            <Radio.Group onChange={this.onRadioGroupChange} buttonStyle="solid">
              <Radio.Button value={'email'}>Email</Radio.Button>
              <Radio.Button value={'phone'}>Teléfono móvil</Radio.Button>
            </Radio.Group>,
          )}
        </Form.Item>
        <Form.Item
          label="Correo electrónico"
          required={this.state.signupMethod === 'email'}
        >
          {getFieldDecorator('email', {
            rules: [
              {
                validator: this.emailValidator,
              },
            ],
          })(<Input />)}
        </Form.Item>
        {this.state.signupMethod === 'email' && (
          <Form.Item
            label="Contraseña"
            required={this.state.signupMethod === 'email'}
          >
            {getFieldDecorator('password', {
              rules: [
                {
                  validator: this.passwordValidator,
                },
              ],
            })(<Input />)}
          </Form.Item>
        )}
        <Form.Item
          label="Teléfono móvil"
          required={this.state.signupMethod === 'phone'}
        >
          {getFieldDecorator('phone', {
            rules: [
              {
                validator: this.phoneValidator,
              },
            ],
          })(<Input addonBefore={prefixSelector} style={{ width: '100%' }} />)}
        </Form.Item>

        <Form.Item label="Tipo" required={true}>
          {getFieldDecorator('type', {
            initialValue: 'collaborator',
            rules: [
              {
                required: true,
                message: 'Por favor, selecciona un tipo',
              },
            ],
          })(
            <Select
              placeholder="Selecciona un tipo"
              onSelect={(value: any) => {
                if (value === 'collaborator') {
                  this.setState({ assignToDrivingCompany: true });
                }
                // if (value === 'own') {
                //   this.setState({ assignToDrivingCompany: false });
                // }
              }}
            >
              <Option key="collaborator" value="collaborator">
                Colaborador
              </Option>
              {/* <Option value="own">Propio</Option> */}
            </Select>,
          )}
        </Form.Item>

        {this.state.assignToDrivingCompany && (
          <Form.Item label="Empresa colaboradora" required={true}>
            {getFieldDecorator('drivingCompanyId', {
              rules: [
                {
                  required: true,
                  message: 'Por favor, selecciona una empresa colaboradora',
                },
              ],
            })(
              <Select
                showSearch
                filterOption={(input, option) => {
                  const optionId = option.props.value;
                  const optionValue = this.props.drivingCompanies.find(
                    (dCompany) =>
                      dCompany.id === optionId ||
                      dCompany.volcanoId === optionId,
                  );

                  if (optionValue) {
                    return (
                      (optionValue.phone || '')
                        .toLowerCase()
                        .includes(input.toLowerCase()) ||
                      (optionValue.name || '')
                        .toLowerCase()
                        .includes(input.toLowerCase()) ||
                      (optionValue.email || '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    );
                  }

                  return false;
                }}
                placeholder="Selecciona una empresa colaboradora"
              >
                {this.props.drivingCompanies.map((drivingCompany) => {
                  return (
                    <Option
                      key={drivingCompany.id || drivingCompany.volcanoId}
                      value={drivingCompany.id || drivingCompany.volcanoId}
                    >
                      {drivingCompany.name}
                    </Option>
                  );
                })}
              </Select>,
            )}
          </Form.Item>
        )}

        <Form.Item label="Estado" required={true}>
          {getFieldDecorator('status', {
            initialValue: 'active',
            rules: [
              {
                required: true,
                message: 'Por favor, selecciona un estado',
              },
            ],
          })(
            <Select placeholder="Selecciona un estado">
              <Option key="active" value="active">
                Activo
              </Option>
              <Option key="inactive" value="inactive">
                Inactivo
              </Option>
            </Select>,
          )}
        </Form.Item>

        <Form.Item label="Foto de perfil" required={false}>
          {getFieldDecorator('upload', {
            valuePropName: 'fileList',
            getValueFromEvent: this.normFile,
          })(
            <AvatarUploader
              onUploadComplete={this.onProfilePicUploadComplete}
              storageRef={'/drivers/profile_pics/'}
            />,
          )}
        </Form.Item>

        <StickyFooterFormButtons>
          <Button
            loading={this.state.creationLoading}
            type="primary"
            htmlType="submit"
            className="login-form-button"
          >
            Crear conductor
          </Button>
        </StickyFooterFormButtons>
      </Form>
    );
  }
}

const MyDriverCreationForm =
  Form.create<DriverCreationFormProps>()(DriverCreationForm);

interface ICreateDriverDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (success?: boolean) => void;
}

export default class CreateDriverDrawer extends React.Component<ICreateDriverDrawerProps> {
  state = {
    drivingCompanies: [],
  };
  componentDidMount = async () => {
    const drivingCompanies = await fetchDrivingCompanies();

    this.setState({
      drivingCompanies,
    });
  };

  onSubmit = (success: boolean) => {
    this.props.onSubmit(success);
  };
  render() {
    return (
      <Drawer
        onClose={this.props.onClose}
        destroyOnClose={true}
        title="Crear conductor colaborador"
        width={'325px'}
        visible={this.props.visible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        {/*<MyDriverCreationForm*/}
        {/*  drivingCompanies={this.state.drivingCompanies}*/}
        {/*  extraProps={{*/}
        {/*    onSubmit: this.onSubmit,*/}
        {/*  }}*/}
        {/*/>*/}
        <MyDriverCreationForm
          drivingCompanies={this.state.drivingCompanies}
          extraProps={{
            onSubmit: this.onSubmit,
          }}
        />
      </Drawer>
    );
  }
}
